import { RequestOverlay } from '@shared/ui/request-overlay';
import { useEffect, useState, type PropsWithChildren } from 'react';

import classes from './load-error-boundary.module.css';

type Props = PropsWithChildren<{
  error?: string | boolean | null;
  onRetry: () => unknown;
  pending?: boolean;
  page?: boolean;
}>;

/** A failed request blocks only the affected content; app navigation stays available. */
export const LoadErrorBoundary = ({ children, error, onRetry, pending = false, page = false }: Props) => {
  const [retryFailed, setRetryFailed] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryRequested, setRetryRequested] = useState(false);
  useEffect(() => {
    if (pending || retrying || !retryRequested) return;
    const timer = window.setTimeout(() => setRetryRequested(false), 0);
    return () => window.clearTimeout(timer);
  }, [pending, retrying, retryRequested]);
  const busy = retrying || (retryRequested && pending);
  const blocked = !!error || busy || retryFailed;
  return (
    <div className={classes.root} data-page={page || undefined}>
      <div className={classes.content} inert={blocked || undefined}>
        {children}
      </div>
      {blocked && (
        <RequestOverlay
          page={page}
          inFlow={!page}
          loading={busy}
          error={error}
          label={busy ? 'Повтор запроса' : 'Ошибка загрузки'}
          onRetry={() => {
            setRetryRequested(true);
            setRetrying(true);
            setRetryFailed(false);
            void (async () => {
              try {
                await onRetry();
              } catch {
                setRetryFailed(true);
              } finally {
                setRetrying(false);
              }
            })();
          }}
        />
      )}
    </div>
  );
};
